import { useRef, useState, useEffect, useCallback } from "react";
import { debounce } from "lodash";
import { Box } from "@mui/material";
import { SERVER_URL } from "../../store/config.js";
import { NOISE_FILTERING } from "../../store/statuses.js";
import { WaveSurferPlayer } from "./WavesurferPlayer";
import { EditorMenu } from "./EditorMenu";
import { useNavigate, useParams } from "react-router-dom";
import {
  filterLoadGUID,
  queueASIFilterRequest,
  queueNLMSFilterRequest,
  queueNormalizeClip,
  queueSyncClip,
} from "../../pages/app/utils/dataLoader.js";
import { editorLoadGUID } from "../../pages/app/utils/dataLoader.js";
import { useGlimpse } from "../../store/glimpse/hooks.js";
import { useNoiseFilter } from "../../store/noiseFilter/hooks.js";
import {
  useEditorPreferences,
  useEditorOperations,
} from "../../store/editor/hooks.js";
import { useAnnotations } from "../../store/annotations/hooks.js";

export default function AudioEditor({
  children,
  clip,
  mode = "editor",
  readOnly,
  regionsPlugin,
  wavesurfer,
}) {
  const minimapRef = useRef();
  const navigate = useNavigate();
  const { caseId, clipId } = useParams();
  const { queueNoiseFilter, reset: resetNoiseFilter } = useNoiseFilter();
  const { savePreferences, preferences } = useEditorPreferences();
  const { mergeRegion, cutRegion } = useEditorOperations();
  const { tagAnnotation } = useAnnotations();
  const [working, setWorking] = useState(false);
  const [surferLoading, setSurferLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(readOnly);
  const { selectedChannel } = preferences;
  const clipLength = Math.floor(clip ? clip.audioFiles[0].duration : 1);
  const { reset: glimpseReset } = useGlimpse();

  const isNoiseFiltering = clip?.status === NOISE_FILTERING;

  const loadTrack = useCallback(
    async (channel = 1) => {
      const guid =
        mode === "sng"
          ? await filterLoadGUID(clip)
          : await editorLoadGUID(clip, channel);
      const track = SERVER_URL + "/api/player/audio/" + guid;
      if (wavesurfer) {
        console.log("loading!");
        await wavesurfer.load(track);
        setSurferLoading(false);
      }
    },
    [clip, mode, wavesurfer],
  );

  useEffect(() => {
    if (wavesurfer) {
      loadTrack(selectedChannel);
    }
  }, [loadTrack, wavesurfer, selectedChannel]);

  const dispatchGlimpseFilter = async (
    glimpseReset,
    navigate,
    caseId,
    clipId,
  ) => {
    await glimpseReset();
    navigate(`/app/cases/${caseId}/clips/${clipId}/glimpse/selectFiles`);
  };

  const dispatchNormalizeFilter = async (navigate, caseId, clipId) => {
    const result = await queueNormalizeClip(caseId, clipId);
    if (result.status === "SUCCESS") {
      navigate(`/app/cases/${caseId}/clips/${clipId}`);
    } else {
      console.log("Error normalizing clip");
    }
  };

  const runSngFilter = async () => {
    setWorking(true);
    setIsReadOnly(true);
    await queueNoiseFilter(caseId, clipId, clip.commitId);
    resetNoiseFilter();
    setWorking(false);
    setIsReadOnly(readOnly);
    navigate(`/app/cases/${caseId}/clips/${clipId}`);
  };

  const runSyncfilter = async (syncFilterRequest) => {
    setWorking(true);
    setIsReadOnly(true);
    console.log("[runSyncfilter] syncFilterRequest", syncFilterRequest);
    await queueSyncClip(caseId, clipId, syncFilterRequest);
    setWorking(false);
    setIsReadOnly(readOnly);
    navigate(`/app/cases/${caseId}/clips/${clipId}`);
  };

  // this one is extract model
  const dispatchMerge = async (tag, comment) => {
    setWorking(true);
    setIsReadOnly(true);
    await mergeRegion(caseId, clipId, tag, comment);
    setWorking(false);
    setIsReadOnly(readOnly);
    navigate(`/app/cases/${caseId}/clips/`);
  };

  const dispatchCut = async (navigate, clipId, tag) => {
    setWorking(true);
    setIsReadOnly(true);
    await cutRegion(caseId, clipId, tag);
    setWorking(false);
    setIsReadOnly(readOnly);
    navigate(`/app/cases/${caseId}/clips/${clipId}`);
  };

  const editorSave = (value) => {
    console.log("editorSave", value);
    savePreferences(caseId, clipId, value);
  };

  const runASIFilter = async (asiFilterRequest) => {
    setWorking(true);
    setIsReadOnly(true);
    console.log("[runAsiFilter] asiFilterRequest", asiFilterRequest);
    await queueASIFilterRequest(caseId, clipId, asiFilterRequest);
    setWorking(false);
    setIsReadOnly(readOnly);
    navigate(`/app/cases/${caseId}/clips/${clipId}`);
  };

  const runNlmsFilter = async (nlmsFilterRequest) => {
    setWorking(true);
    setIsReadOnly(true);
    console.log("[runNlmsFilter] nlmsFilterRequest", nlmsFilterRequest);
    await queueNLMSFilterRequest(caseId, clipId, nlmsFilterRequest);
    setWorking(false);
    setIsReadOnly(readOnly);
    navigate(`/app/cases/${caseId}/clips/${clipId}`);
  };

  const editorSaveDebounce = debounce(editorSave, 250, { trailing: true });

  return (
    <Box
      display="flex"
      flexDirection="column"
      width={1}
      height="calc(100vh - 128px)"
    >
      <EditorMenu
        caseId={caseId}
        clipLength={clipLength}
        surferLoading={surferLoading}
        numberOfChannels={clip.numberOfChannels}
        sampleRate={clip?.audioFiles[0]?.sampleRate || 1}
        readOnly={isReadOnly}
        working={working}
        mode={mode}
        onTagRegion={(tag) => tagAnnotation(caseId, clipId, tag)}
        onCutRegion={(tag) => dispatchCut(navigate, clipId, tag)}
        onMergeRegion={(tag, comment) => dispatchMerge(tag, comment)}
        onGlimpseFilter={() =>
          dispatchGlimpseFilter(glimpseReset, navigate, caseId, clipId)
        }
        onNormalizeFilter={() =>
          dispatchNormalizeFilter(navigate, caseId, clipId)
        }
        onSngFilter={() => runSngFilter()}
        onSyncFilter={(formValues) => runSyncfilter(formValues)}
        syncSelected={clip?.sync}
        onAsiFilter={(formValues) => runASIFilter(formValues)}
        onNlmsFilter={(formValues) => runNlmsFilter(formValues)}
        isNoiseFiltering={isNoiseFiltering}
      />
      <Box display="flex" flexDirection="column" height={1} m={2} mt={0}>
        <Box
          ref={minimapRef}
          sx={{
            backgroundColor: "#FFF",
            margin: "auto",
            width: "calc(100% - 100px)",
          }}
        >
          {children[0]}
        </Box>
        <WaveSurferPlayer
          preferencesSave={editorSaveDebounce}
          clip={clip}
          barGap={0}
          waveColor="rgb(31, 78, 179, 1)"
          progressColor="rgb(31, 78, 179, 1)"
          cursorColor="rgb(255, 0, 0)"
          readOnly={isReadOnly}
          wavesurfer={wavesurfer}
          regionsPlugin={regionsPlugin}
          mode={mode}
        >
          {children[1]}
        </WaveSurferPlayer>
      </Box>
    </Box>
  );
}
